import { useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { dateTime } from '@/lib/format'
import { proposedLastFour } from '@/lib/commission'
import { useAsync } from '@/lib/useAsync'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input, Textarea } from '@/components/ui/form'
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type { BankAccountView, ChangeRequestView } from '@/types/api'

/**
 * Where a partner is paid, and the change nobody can make alone.
 *
 * <p>
 * The threat this screen is built around is not a stolen database - Azure SQL encrypts
 * at rest. It is a <i>changed number</i>: whoever compromises a portal account wants to
 * write a new account, and the next payout goes to them. So the account in force stays
 * in force until somebody at Codemax approves the change, and the approver confirms by
 * telephoning the number already on file rather than the one in the request.
 * </p>
 */
export function ResellerBank({
  resellerId,
  phoneOnFile,
}: {
  resellerId: string
  /** Printed in the approval, because it is the number the approver must ring. */
  phoneOnFile: string | null | undefined
}) {
  const { can } = useAuth()
  const [approving, setApproving] = useState<ChangeRequestView | undefined>()
  const [rejecting, setRejecting] = useState<ChangeRequestView | undefined>()
  const [revealed, setRevealed] = useState<Record<string, string>>({})

  const accounts = useAsync<BankAccountView[]>(
    (signal) => api.get<BankAccountView[]>(`/resellers/${resellerId}/bank-accounts`, undefined, signal),
    [resellerId],
  )

  const requests = useAsync<ChangeRequestView[]>(
    (signal) =>
      api.get<ChangeRequestView[]>(
        '/reseller-change-requests',
        // pendingOnly is the default, and this tab shows the history as well as what is
        // waiting - a partner who asked twice and was refused once is the context an
        // approver needs before approving the third.
        { resellerId, pendingOnly: false },
        signal,
      ),
    [resellerId],
  )

  if (accounts.loading && !accounts.data) return <Loading />
  if (accounts.error) {
    return (
      <ErrorState title={accounts.error.title} detail={accounts.error.detail} onRetry={accounts.reload} />
    )
  }

  const pending = (requests.data ?? []).filter((request) => request.status === 'Pending')
  const reviewed = (requests.data ?? []).filter((request) => request.status !== 'Pending')
  const canApprove = can('reseller.bank.approve')

  async function reveal(account: BankAccountView) {
    try {
      const secret = await api.get<{ id: string; accountNumber: string }>(
        `/resellers/${resellerId}/bank-accounts/${account.id}/number`,
      )
      setRevealed((current) => ({ ...current, [account.id]: secret.accountNumber }))
    } catch {
      // Deliberately quiet. The button is only rendered for somebody who holds the
      // permission, so a failure here is a network problem rather than a refusal to
      // explain, and the masked number is still on the screen.
    }
  }

  return (
    <>
      {pending.length > 0 && (
        <div className="mb-5 rounded-md border border-warn/30 bg-warn-wash px-4 py-3 text-[13px] text-warn">
          <b>{pending.length === 1 ? 'A change is' : `${pending.length} changes are`} waiting.</b>{' '}
          Payouts continue to the account in force until somebody approves it.
        </div>
      )}

      <Card className="mb-5">
        <CardHeader
          title="Bank accounts"
          hint="Only the last four digits leave the API, except on one route behind reseller.bank.view."
        />
        {(accounts.data ?? []).length === 0 ? (
          <EmptyState
            title="No bank account"
            hint="A partner proposes one from their portal. Until one is in force there is nowhere to send a payout."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Bank</Th>
                <Th>Holder</Th>
                <Th>Currency</Th>
                <Th>Number</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {(accounts.data ?? []).map((account) => (
                <Tr key={account.id}>
                  <Td>
                    {account.bankName}
                    {account.branchName && (
                      <span className="block text-[12px] text-muted">{account.branchName}</span>
                    )}
                  </Td>
                  <Td>{account.accountHolderName}</Td>
                  <Td className="tabular">{account.currencyCode}</Td>
                  <Td className="tabular">
                    {revealed[account.id] ?? `•••• •••• ${account.lastFour}`}
                  </Td>
                  <Td>
                    <Badge
                      tone={
                        account.status === 'Active'
                          ? 'positive'
                          : account.status === 'Pending'
                            ? 'warning'
                            : 'neutral'
                      }
                    >
                      {account.status === 'Active' ? 'In force' : account.status}
                    </Badge>
                  </Td>
                  <Td className="text-right">
                    {can('reseller.bank.view') && !revealed[account.id] && (
                      <Button onClick={() => reveal(account)}>Reveal number</Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card className="mb-5">
        <CardHeader
          title="Change requests"
          hint="A partner proposes and cannot make a change. Approving is what puts one in force."
        />
        {pending.length === 0 && reviewed.length === 0 ? (
          <EmptyState title="Nothing requested" hint="This partner has not asked for a change." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Asked</Th>
                <Th>What</Th>
                <Th>Proposed</Th>
                <Th>By</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {[...pending, ...reviewed].map((request) => (
                <Tr key={request.id}>
                  <Td>{dateTime(request.requestedAt)}</Td>
                  <Td>{request.kind === 'BankAccount' ? 'Bank account' : 'Company details'}</Td>
                  <Td className="text-[12px] text-muted">{request.proposedSummary}</Td>
                  <Td>{request.requestedBy}</Td>
                  <Td>
                    <Badge
                      tone={
                        request.status === 'Approved'
                          ? 'positive'
                          : request.status === 'Rejected'
                            ? 'critical'
                            : 'warning'
                      }
                    >
                      {request.status}
                    </Badge>
                  </Td>
                  <Td className="text-right">
                    {request.status === 'Pending' && canApprove && (
                      <div className="flex justify-end gap-2">
                        <Button variant="primary" onClick={() => setApproving(request)}>
                          Approve
                        </Button>
                        <Button variant="danger" onClick={() => setRejecting(request)}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <ApproveChange
        request={approving}
        phoneOnFile={phoneOnFile}
        onClose={() => setApproving(undefined)}
        onDone={() => {
          setApproving(undefined)
          accounts.reload()
          requests.reload()
        }}
      />

      <RejectChange
        request={rejecting}
        onClose={() => setRejecting(undefined)}
        onDone={() => {
          setRejecting(undefined)
          requests.reload()
        }}
      />
    </>
  )
}

function ApproveChange({
  request,
  phoneOnFile,
  onClose,
  onDone,
}: {
  request?: ChangeRequestView
  phoneOnFile: string | null | undefined
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [confirmed, setConfirmed] = useState('')
  const [who, setWho] = useState('')
  const [saving, setSaving] = useState(false)

  const bank = request?.kind === 'BankAccount'
  const expected = request ? proposedLastFour(request.proposedSummary) : ''

  // Retyping is only asked for where it means something. A profile correction does not
  // move money, and a confirmation box on it would train people to type past this one.
  const matches = !bank || expected === '' || confirmed.trim() === expected

  async function approve() {
    if (!request) return
    setSaving(true)
    try {
      await api.post(`/reseller-change-requests/${request.id}/approve`, {
        note: who.trim() === '' ? null : `Confirmed by telephone with ${who.trim()}`,
      })
      toast.show('Change approved', 'success')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not approve the change', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={request !== undefined}
      title={bank ? 'Approve a new bank account' : 'Approve a change'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={approve} disabled={saving || !matches}>
            {saving ? 'Approving...' : 'Approve and put in force'}
          </Button>
        </>
      }
    >
      {bank && (
        <p className="mb-4 rounded-md border border-crit/30 bg-crit-wash px-4 py-3 text-[13px] text-crit">
          Telephone <b className="tabular">{phoneOnFile ?? 'the number already on file'}</b> &mdash;
          the number already on this partner&rsquo;s file. <b>Never a number in the request.</b>{' '}
          Whoever asked for this change may be whoever took over the account.
        </p>
      )}

      <p className="mb-4 text-[13px] text-muted">{request?.proposedSummary}</p>

      <div className="flex flex-col gap-4">
        <Field label="Who confirmed it, by telephone" hint="Goes on the audit record.">
          {(id) => <Input id={id} value={who} onChange={(e) => setWho(e.target.value)} />}
        </Field>

        {bank && expected !== '' && (
          <Field
            label="Type the last four digits of the new account"
            required
            hint="The same four you just read down the telephone."
          >
            {(id) => (
              <Input
                id={id}
                inputMode="numeric"
                value={confirmed}
                onChange={(e) => setConfirmed(e.target.value)}
              />
            )}
          </Field>
        )}
      </div>
    </Dialog>
  )
}

function RejectChange({
  request,
  onClose,
  onDone,
}: {
  request?: ChangeRequestView
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  async function reject() {
    if (!request) return
    setSaving(true)
    try {
      await api.post(`/reseller-change-requests/${request.id}/reject`, {
        note: note.trim() === '' ? null : note.trim(),
      })
      toast.show('Change rejected', 'success')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not reject the change', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={request !== undefined}
      title="Reject this change"
      description="The partner sees the note, so say what would make the request acceptable."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={reject} disabled={saving}>
            {saving ? 'Rejecting...' : 'Reject'}
          </Button>
        </>
      }
    >
      <Field label="Note to the partner">
        {(id) => <Textarea id={id} rows={3} value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
    </Dialog>
  )
}
